/**
 * Pure-function tab visibility (and authentication locking) for
 * `<IrisConnectionSection>`.
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The rules:
 *
 *   - Authentication is ALWAYS visible (URX flow shows the form;
 *     Iris-enabled flow shows a disabled informational state). The
 *     form is rendered either way because the tab is the deep-link
 *     target for the per-tile chip's "Authenticate" verb, regardless
 *     of device type.
 *   - Video / Control / Driver are HIDDEN in embedded contexts
 *     (preserves existing behavior — these tabs are only relevant
 *     for non-embedded URX paths).
 *   - Monitoring is ALWAYS visible (preserves existing behavior).
 *   - For a TRANSITORY placeholder (Multi-Add Phase 2, spec §4.1) ONLY
 *     Authentication is visible (Video/Control/Driver/Monitoring hidden)
 *     — there is no real device to configure until the placeholder is
 *     authenticated.
 *
 * Per `think-in-systems` (ai.knowledge/global): extracting the filter
 * to a pure function lets the visibility contract be unit-tested
 * without rendering the full `<IrisConnectionSection>` (which depends
 * on the analytics + agent stores).
 */
import type { IrisConnectionTab } from './iris-connection-section.js';

/** Item shape stored in the SECONDARY_TABS array. */
export type TabConfig = { value: IrisConnectionTab; label: string };

/**
 * Filter `tabs` to those visible in the given context.
 *
 * @param tabs - Full ordered tab list (unfiltered).
 * @param isEmbeddedContext - Whether the current device/agent is an
 *   embedded Iris instance.
 * @param isTransitory - Whether the candidate is an unauthenticated
 *   transitory placeholder (spec §4.1). When true, ONLY the
 *   Authentication tab renders — there is no real device to configure
 *   yet. Defaults to `false` so existing call sites are unaffected.
 * @returns Tabs that should render.
 */
export const filterAvailableTabs = (
  tabs: ReadonlyArray<TabConfig>,
  isEmbeddedContext: boolean,
  isTransitory = false
): TabConfig[] =>
  tabs.filter((tab) => {
    // Transitory placeholder (§4.1): only Authentication is meaningful —
    // hide every other tab regardless of embedded context.
    if (isTransitory) return tab.value === 'authentication';
    // Authentication tab is always visible.
    if (tab.value === 'authentication') return true;
    // Existing rule: video/control/driver hidden in embedded contexts.
    if (
      isEmbeddedContext &&
      (tab.value === 'video' || tab.value === 'control' || tab.value === 'driver')
    ) {
      return false;
    }
    return true;
  });

/**
 * Whether `tab` is locked: still shown, but unusable until the camera accepts
 * its credentials. Authentication never locks — it is how the user unlocks the
 * other tabs.
 *
 * @param tab - Tab to check.
 * @param authenticationPending - Whether the camera is waiting for credentials
 *   (`useAuthenticationPending`).
 * @returns `true` when the tab must refuse activation.
 */
export const isTabLocked = (tab: IrisConnectionTab, authenticationPending: boolean): boolean =>
  authenticationPending && tab !== 'authentication';
